import { useQuery } from "@tanstack/react-query";
import type { RecipeInput } from "./api";

/**
 * The style guide the recipe builder offers (Scott, 2026-10-02): the Brewers Association's GABF 2026
 * guidelines, which Scott says breweries work from, not the BJCP's. `src/data/gabf-2026.json` is made from
 * the guidelines' PDF by `scripts/gabfStyles.mjs`, and is loaded only when the builder opens.
 */

export type RecipeStyle = NonNullable<RecipeInput["style"]>;
type Range = { min: number; max: number } | null;
export type Figure = "og" | "fg" | "abv" | "ibu" | "srm";

export interface GuideStyle {
  name: string;
  guide: string;
  /** The guide's number and letter, "68" or "82B". */
  code: string;
  category: string;
  /** The guide's section, "Ale Beer Styles". */
  group: string;
  describe: Partial<
    Record<
      "color" | "clarity" | "malt" | "hops" | "bitterness" | "fermentation" | "body" | "notes",
      string
    >
  >;
  og: Range;
  fg: Range;
  abv: Range;
  ibu: Range;
  srm: Range;
  /** Each figure as the guide words it, for one that is not a plain range ("40+", "Varies with style"). */
  as: Partial<Record<Figure, string>>;
}

export function useStyleGuide() {
  return useQuery({
    queryKey: ["style-guide", "gabf-2026"],
    queryFn: async () => (await import("./data/gabf-2026.json")).default as GuideStyle[],
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** A guide's style as a recipe keeps it: its name, where it comes from and its ranges. */
export const recipeStyleOf = (s: GuideStyle): RecipeStyle => ({
  name: s.name,
  guide: s.guide,
  code: s.code,
  category: s.category,
  og: s.og,
  fg: s.fg,
  abv: s.abv,
  ibu: s.ibu,
  srm: s.srm,
});

/** The guide's entry for a recipe's style, when the style was picked from it. */
export const guideEntry = (guide: GuideStyle[], style: RecipeStyle | null | undefined) =>
  style?.guide && style.code
    ? guide.find((s) => s.guide === style.guide && s.code === style.code)
    : undefined;
